import React, { Suspense, useRef } from "react";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { Stars, Sparkles, Grid } from "@react-three/drei";
import { ARENA, TEAM } from "../game/theme";

// Gentle mouse-parallax camera that always looks at the stage.
function CameraRig({ base = [0, 4.5, 11], look = [0, 2, -2], sway = 1.2 }) {
  const { camera, pointer } = useThree();
  useFrame((_, dt) => {
    const t = Math.min(dt * 2.5, 1);
    camera.position.x += (base[0] + pointer.x * sway - camera.position.x) * t;
    camera.position.y += (base[1] + pointer.y * sway * 0.5 - camera.position.y) * t;
    camera.position.z += (base[2] - camera.position.z) * t;
    camera.lookAt(look[0], look[1], look[2]);
  });
  return null;
}

// Pulsing neon ring that frames the stage.
function NeonRing({ radius = 9, color = "#4455ff", y = 0.02, speed = 1.4 }) {
  const mat = useRef();
  useFrame(({ clock }) => {
    if (mat.current) {
      mat.current.emissiveIntensity =
        1.4 + Math.sin(clock.elapsedTime * speed) * 0.6;
    }
  });
  return (
    <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, y, 0]}>
      <torusGeometry args={[radius, 0.07, 12, 96]} />
      <meshStandardMaterial
        ref={mat}
        color={color}
        emissive={color}
        emissiveIntensity={1.6}
        toneMapped={false}
      />
    </mesh>
  );
}

// Team-colored light strips along each side of the arena floor.
function TeamStrip({ side = -1, color }) {
  const mat = useRef();
  useFrame(({ clock }) => {
    if (mat.current) {
      mat.current.emissiveIntensity =
        1.2 + Math.sin(clock.elapsedTime * 2 + side) * 0.5;
    }
  });
  return (
    <mesh position={[side * 8.5, 0.04, 0]} rotation={[-Math.PI / 2, 0, 0]}>
      <planeGeometry args={[0.35, 12]} />
      <meshStandardMaterial
        ref={mat}
        color={color}
        emissive={color}
        emissiveIntensity={1.4}
        toneMapped={false}
      />
    </mesh>
  );
}

export function ArenaEnvironment({ ringColor = "#4455ff" }) {
  return (
    <>
      <color attach="background" args={[ARENA.bg]} />
      <fog attach="fog" args={[ARENA.fog, 18, 42]} />
      <ambientLight intensity={0.5} />
      <directionalLight position={[6, 12, 8]} intensity={0.9} />
      <pointLight position={[-8, 5, 2]} intensity={40} color={TEAM.left.color} />
      <pointLight position={[8, 5, 2]} intensity={40} color={TEAM.right.color} />
      <pointLight position={[0, 8, -4]} intensity={50} color="#8899ff" />

      <Stars radius={80} depth={40} count={2500} factor={4} saturation={0.4} fade speed={0.6} />
      <Sparkles count={60} scale={[24, 10, 24]} size={2.5} speed={0.35} position={[0, 5, -2]} color="#88aaff" />

      {/* Stage floor */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -0.01, 0]}>
        <circleGeometry args={[9, 64]} />
        <meshStandardMaterial color={ARENA.floor} metalness={0.6} roughness={0.4} />
      </mesh>
      <NeonRing radius={9} color={ringColor} />
      <TeamStrip side={-1} color={TEAM.left.color} />
      <TeamStrip side={1} color={TEAM.right.color} />

      <Grid
        position={[0, -0.02, 0]}
        args={[80, 80]}
        cellSize={1}
        cellThickness={0.4}
        cellColor={ARENA.grid}
        sectionSize={5}
        sectionThickness={1}
        sectionColor="#3d3d7a"
        fadeDistance={45}
        fadeStrength={1.5}
        infiniteGrid
      />
    </>
  );
}

export function ArenaCanvas({ children, cameraBase, cameraLook, sway, ringColor }) {
  return (
    <Canvas
      dpr={[1, 2]}
      camera={{ position: cameraBase || [0, 4.5, 11], fov: 50 }}
      style={{ position: "fixed", inset: 0 }}
    >
      <Suspense fallback={null}>
        <ArenaEnvironment ringColor={ringColor} />
        <CameraRig base={cameraBase} look={cameraLook} sway={sway} />
        {children}
      </Suspense>
    </Canvas>
  );
}
